// Avvisi task assegnati: banner sotto la topbar + pallino accanto al pulsante sync.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ClipboardList, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  myPendingTickets, ticketSeverity, worstSeverity, isTaskBannerDismissed, dismissTaskBanner,
  subscribeSupport, hydrateSupport, isSupportReady, PRIORITIES,
} from "@/lib/supportTicketsData";
import { fmtDate } from "@/lib/formatDate";

const SEV_DOT = { red: "bg-red-500", orange: "bg-amber-500", green: "bg-emerald-500" };
const SEV_ROW = {
  red: "border-red-500/30 bg-red-500/10",
  orange: "border-amber-500/30 bg-amber-500/10",
  green: "border-emerald-500/30 bg-emerald-500/10",
};
const SEV_TEXT = {
  red: "text-red-600 dark:text-red-400",
  orange: "text-amber-600 dark:text-amber-400",
  green: "text-emerald-700 dark:text-emerald-400",
};

function usePendingAssigned() {
  const { user } = useAuth();
  const [, force] = useState(0);
  useEffect(() => {
    if (!isSupportReady()) hydrateSupport();
    return subscribeSupport(() => force((x) => x + 1));
  }, []);
  return myPendingTickets(user?.id);
}

// Pallino colorato accanto al pulsante sync: solo task assegnati non presi in carico.
export function PendingTasksDot() {
  const pending = usePendingAssigned();
  if (!pending.length) return null;
  const sev = worstSeverity(pending);
  return (
    <span
      data-testid="pending-tasks-dot"
      title={`${pending.length} task assegnat${pending.length === 1 ? "o" : "i"} da prendere in carico`}
      className={`pointer-events-none absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-background ${SEV_DOT[sev]}`}
    />
  );
}

// Banner fisso sotto la barra di ricerca: un avviso per ogni task assegnato.
export function AssignedTasksBanner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [, force] = useState(0);
  const pending = usePendingAssigned().filter((t) => !isTaskBannerDismissed(t, user?.id));
  if (!pending.length) return null;
  const dismiss = (t) => { dismissTaskBanner(t, user?.id); force((x) => x + 1); };
  return (
    <div className="space-y-1.5 border-b border-border/60 bg-background/80 px-4 py-2 backdrop-blur-xl sm:px-6" data-testid="assigned-tasks-banner">
      {pending.slice(0, 4).map((t) => {
        const sev = ticketSeverity(t);
        return (
          <div key={t.id} className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 text-sm ${SEV_ROW[sev]}`} data-testid={`assigned-task-row-${t.id}`}>
            <ClipboardList className={`h-4 w-4 shrink-0 ${SEV_TEXT[sev]}`} />
            <span className="min-w-0 flex-1 truncate">
              <span className={`font-medium ${SEV_TEXT[sev]}`}>Task assegnato a te:</span>{" "}
              <span className="text-foreground">{t.request_subject}</span>
              <span className="text-muted-foreground"> · {t.company_name || "—"} · priorità {PRIORITIES[t.priority]?.label || "—"}{t.due_date ? ` · scade ${fmtDate(t.due_date)}` : ""}</span>
            </span>
            <button
              type="button"
              onClick={() => navigate("/tasks/support-tickets")}
              className={`flex shrink-0 items-center gap-1 text-xs font-medium hover:underline ${SEV_TEXT[sev]}`}
              data-testid={`assigned-task-open-${t.id}`}
            >
              Apri <ArrowRight className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => dismiss(t)}
              className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
              title="Nascondi (riappare se il task viene riassegnato)"
              data-testid={`assigned-task-dismiss-${t.id}`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
      {pending.length > 4 && (
        <div className="px-1 text-xs text-muted-foreground" data-testid="assigned-tasks-more">
          + altri {pending.length - 4} task assegnati — <button type="button" className="underline" onClick={() => navigate("/tasks/support-tickets")}>vedi tutti</button>
        </div>
      )}
    </div>
  );
}
